import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Hammer,
  Wrench,
  ClipboardCheck,
} from "lucide-react";

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              Commercial Interiors, Built Around Your Business
            </motion.h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Specialists in office fit-out, refurbishment and workspace
              upgrades across the South East.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <Link
                to="/contact#contact-form"
                onClick={() => {
                  sessionStorage.setItem("scrollToContactForm", "true");
                }}
                className="btn-primary inline-flex items-center justify-center space-x-2"
              >
                <span>Get a Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                className="border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white font-medium py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center justify-center space-x-2"
              >
                <span>View Our Services</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Intro Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <div className="space-y-6 text-lg text-gray-700 italic">
              <p>
                For over 30 years, Sandhurst Interiors has delivered
                high-quality commercial fit-outs and refurbishment projects for
                clients across the South East of England.
              </p>
              <p>
                As a family-run business, we combine traditional craftsmanship
                with modern solutions—ensuring every space is built to last and
                tailored to how you work.
              </p>
              <p>
                More than 70% of our projects come from happy returning clients.
                We earn this loyalty thanks to the relationships we build, the
                reliability we deliver, and the quality we maintain on every
                job, regardless of size or budget.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Us
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Award,
                title: "Experience You Can Trust",
                description:
                  "Three decades in commercial interiors, with proven results across offices, warehouses, and industrial facilities.",
              },
              {
                icon: Hammer,
                title: "Quality Craftsmanship",
                description:
                  "All contractors are time-served professionals committed to delivering precision finishes.",
              },
              {
                icon: Wrench,
                title: "Flexible Solutions",
                description:
                  "From facilities maintenance to full turnkey office transformations—we scale to your needs.",
              },
              {
                icon: ClipboardCheck,
                title: "End-to-End Project Management",
                description:
                  "Dedicated site supervision, planning, and coordination to keep your project running smoothly.",
              },
            ].map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                }}
                className="card text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What We Do
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We handle all aspects of commercial interior refurbishment and
              fit-out, including:
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Office partitioning (soundproof, glazed, steel, timber)",
              "Dry-lining, taping & jointing and plastering",
              "Suspended ceilings and MF plasterboard ceilings",
              "Interior & exterior decorating",
              "Flooring installations (carpet tiles, vinyl, specialist finishes)",
              "Air conditioning & ventilation systems (design & installation)",
              "Electrical & data installation & maintenance",
              "Facilities management (planned & reactive maintenance)",
              "Office / warehouse space planning",
              "Full project management",
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.4,
                  delay: index * 0.05,
                }}
                className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">{service}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to improve your workspace?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about transforming your office,
              warehouse or facility.
            </p>
            <div className="flex justify-center">
              <Link
                to="/contact#contact-form"
                onClick={() => {
                  sessionStorage.setItem("scrollToContactForm", "true");
                }}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 text-center"
              >
                Get in Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
